import React, { useState } from 'react';

import { useSnackbar } from 'notistack';

import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import PageSubheader from '~/components/Common/Elements/PageSubheader';
import PermissionedComponent from '~/components/Common/PermissionedComponent';

import { ROLE_ADMIN_USER } from '~/consts/roles';

import { useGetAccountIdentityUsers } from '~/hooks/apis/users/useGetAccountIdentityUsers';
import { useMarkAccountIdentityEmailVerified } from '~/hooks/apis/users/useMarkAccountIdentityEmailVerified';

import { formatDateOrdinalWithClock } from '~/services/TimeService';
import type { AccountIdentityUser } from '~/services/UsersService';

import { getErrorMessage } from './utils';

function renderField(label: string, value: React.ReactNode) {
  return (
    <Box>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Box>{value}</Box>
    </Box>
  );
}

function renderEmailVerified(emailVerified: boolean | null) {
  if (emailVerified === null) {
    return <Typography>-</Typography>;
  }
  return emailVerified ? (
    <Chip size="small" color="success" label="Verified" />
  ) : (
    <Chip size="small" color="warning" label="Not verified" />
  );
}

function getAttribute(user: AccountIdentityUser, name: string) {
  return user.attributes.find((attribute) => attribute.name === name)?.value;
}

interface Props {
  accountId: string;
}

export default function IdentityCognitoUsers({ accountId }: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const { data: users = [], error } = useGetAccountIdentityUsers(accountId);
  const markEmailVerified = useMarkAccountIdentityEmailVerified(accountId);
  const [userPendingVerify, setUserPendingVerify] = useState<AccountIdentityUser | null>(null);

  const handleMarkEmailVerified = async () => {
    if (!userPendingVerify) {
      return;
    }

    try {
      await markEmailVerified.mutateAsync(userPendingVerify.identity_id);
      setUserPendingVerify(null);
      enqueueSnackbar('Cognito email marked as verified', { variant: 'success' });
    } catch (error) {
      enqueueSnackbar(`Unable to mark the email as verified: ${getErrorMessage(error)}`, { variant: 'error' });
    }
  };

  if (error) {
    return (
      <Paper component="section" variant="outlined" sx={{ p: 3, mt: 3 }}>
        <PageSubheader title="Cognito user" />
        <Alert severity="error">Unable to load the Cognito user: {getErrorMessage(error)}</Alert>
      </Paper>
    );
  }

  return (
    <>
      {users.map((user) => (
        <Paper key={user.identity_id} component="section" variant="outlined" sx={{ p: 3, mt: 3 }}>
          <PageSubheader title="Cognito user" />
          <Stack direction="row" flexWrap="wrap" gap={3} sx={{ mb: 2 }}>
            {renderField('User pool ID', <Typography>{user.user_pool_id ?? '-'}</Typography>)}
            {renderField('Username', <Typography>{user.username}</Typography>)}
            {renderField('Identity provider', <Typography>{user.identity_provider_name}</Typography>)}
            {renderField('Status', <Typography>{user.status ?? '-'}</Typography>)}
            {renderField(
              'Enabled',
              <Typography>{user.enabled === null ? '-' : user.enabled ? 'Yes' : 'No'}</Typography>,
            )}
            {renderField(
              'Email verified',
              <Stack direction="row" alignItems="center" gap={1}>
                {renderEmailVerified(user.email_verified)}
                {!user.email_verified && !user.federated && (
                  <PermissionedComponent requiredRoles={[ROLE_ADMIN_USER]}>
                    <Button
                      size="small"
                      onClick={() => setUserPendingVerify(user)}
                      disabled={markEmailVerified.isPending}
                    >
                      Mark email as verified
                    </Button>
                  </PermissionedComponent>
                )}
              </Stack>,
            )}
            {renderField(
              'MFA',
              <Typography>{user.mfa_methods.length ? user.mfa_methods.join(', ') : 'None'}</Typography>,
            )}
            {renderField(
              'Created',
              <Typography>{user.created_at ? formatDateOrdinalWithClock(user.created_at) : '-'}</Typography>,
            )}
            {renderField(
              'Last modified',
              <Typography>{user.updated_at ? formatDateOrdinalWithClock(user.updated_at) : '-'}</Typography>,
            )}
          </Stack>

          {user.federated && (
            <Alert severity="info" sx={{ mb: 2 }}>
              This user signs in through SSO, so their email is managed by their organisation&apos;s directory.
            </Alert>
          )}

          <Table size="small" aria-label="Cognito user attributes">
            <TableHead>
              <TableRow>
                <TableCell>Attribute</TableCell>
                <TableCell>Value</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {user.attributes.map((attribute) => (
                <TableRow key={attribute.name}>
                  <TableCell>{attribute.name}</TableCell>
                  <TableCell sx={{ wordBreak: 'break-all' }}>{attribute.value}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      ))}

      <Dialog
        open={Boolean(userPendingVerify)}
        onClose={markEmailVerified.isPending ? undefined : () => setUserPendingVerify(null)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Mark email as verified?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" gutterBottom>
            Cognito will send password-reset codes to{' '}
            <strong>{(userPendingVerify && getAttribute(userPendingVerify, 'email')) ?? 'this email'}</strong>.
          </Typography>
          <Typography variant="body2">Before continuing, confirm the user owns this email address.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUserPendingVerify(null)} disabled={markEmailVerified.isPending}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleMarkEmailVerified} disabled={markEmailVerified.isPending}>
            Mark as verified
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
